export const COMPLAINTS_HANDLING_POLICY = {
  id: 'complaints-handling-policy',
  title: 'Complaints Handling Policy',
  version: 'v1.0-2026-05-17',
  effectiveDate: '17/05/2026',
  audience: 'all',
  content: `COMPLAINTS HANDLING POLICY — RESERVO

Status: DRAFT — pending solicitor review

1. PURPOSE

This policy describes how Reservo receives, investigates, and resolves complaints concerning its service, its Drivers, or its personnel.

2. PRINCIPLES

2.1 Reservo's complaint handling is:
  (a) accessible — multiple channels for submission;
  (b) prompt — defined service-level commitments;
  (c) fair — both complainant and respondent are heard;
  (d) confidential — information shared only with those needing it;
  (e) free of retaliation — no penalty for good-faith complaint;
  (f) compliant — escalation to regulators where required.

3. SCOPE

3.1 This policy covers complaints about:
  (a) Driver conduct (safety, courtesy, discrimination, fraud);
  (b) trip quality (route, timing, vehicle condition);
  (c) fare or payment matters;
  (d) platform issues (app failure, lost feature, miscommunication);
  (e) accessibility refusal;
  (f) handling of personal information;
  (g) any other matter affecting the complainant's use of the service.

3.2 This policy does not cover:
  (a) emergencies (covered by Emergency Response Procedure);
  (b) incident reports of Notifiable Occurrences (covered by Incident Reporting Policy);
  (c) data breaches (covered by Data Breach Response Plan).

4. COMPLAINT CATEGORIES

4.1 Reservo classifies complaints into the following categories:
  (a) service quality;
  (b) driver conduct;
  (c) safety;
  (d) discrimination;
  (e) accessibility;
  (f) fare or payment;
  (g) platform / technical;
  (h) privacy;
  (i) other.

5. SUBMISSION CHANNELS

5.1 Complaints may be submitted via:
  (a) the in-app complaints feature (preferred);
  (b) admin@reservo.au;
  (c) 0435 679 630 during business hours.

5.2 Each submission is logged in the complaints register with a unique reference number.

6. SERVICE LEVELS

6.1 Acknowledgement: every complaint is acknowledged within 24 hours of receipt by the same channel used for submission.

6.2 Initial substantive response: within 7 days of receipt, including:
  (a) confirmation of category and severity;
  (b) the responsible Reservo officer;
  (c) the next steps;
  (d) expected resolution date.

6.3 Resolution: target within 28 days. Where resolution requires longer (complex investigation, regulatory involvement, third-party input), the complainant is informed of the reason and the revised expected timeline.

6.4 Critical-severity complaints (immediate safety risk, sexual misconduct, child safety, serious assault) are prioritised and may receive same-day response.

7. INVESTIGATION

7.1 The Reservo Operations team:
  (a) assesses the complaint;
  (b) gathers evidence (in-app messages, ratings, GPS, dashcam where available, witness statements, prior history);
  (c) provides the respondent (typically a Driver) with a fair opportunity to respond;
  (d) reaches a conclusion based on the evidence;
  (e) records findings in the complaints register.

7.2 Where the respondent is an employee or contractor of Reservo, the investigation is conducted by an officer not involved in the underlying matter.

8. POSSIBLE OUTCOMES

8.1 Outcomes may include:
  (a) finding of no fault — no action;
  (b) apology;
  (c) refund or credit;
  (d) warning to the respondent;
  (e) additional training required;
  (f) suspension or termination of the respondent;
  (g) referral to the NSW Point to Point Transport Commissioner;
  (h) referral to NSW Police;
  (i) referral to anti-discrimination authorities;
  (j) referral to the Office of the Australian Information Commissioner.

9. ESCALATION

9.1 A complainant who is not satisfied with Reservo's response may escalate to:
  (a) the NSW Point to Point Transport Commissioner — pointtopoint.nsw.gov.au — for transport-service matters;
  (b) the Australian Human Rights Commission — humanrights.gov.au — for discrimination matters;
  (c) the NSW Anti-Discrimination Board — antidiscrimination.justice.nsw.gov.au — for discrimination matters;
  (d) the Office of the Australian Information Commissioner — oaic.gov.au — for privacy matters;
  (e) NSW Civil and Administrative Tribunal (NCAT) — ncat.nsw.gov.au — for consumer-law matters;
  (f) NSW Police — for criminal matters.

9.2 Reservo cooperates with all such escalations.

10. RECORDS

10.1 Every complaint and its handling is recorded in the complaints register, retained for 7 years from the date of resolution.

10.2 Trend analysis is performed quarterly to identify systemic issues.

11. PROTECTION FROM RETALIATION

11.1 Reservo prohibits retaliation against any person making a complaint in good faith.

11.2 A Driver found to have retaliated against a complainant faces immediate suspension or termination.

12. WORKING WITH ASSISTANCE

12.1 Reservo provides complaint-submission support to:
  (a) people with disability (alternative submission channels, plain language);
  (b) people with limited English (assistance through admin@reservo.au);
  (c) minors who travelled as passengers (their adult Rider may submit on their behalf);
  (d) emergency-contact persons in life-threatening matters.

13. PRIVACY

13.1 Complaint information is confidential and shared only with persons necessary for investigation and resolution.

13.2 The complainant's identity is shared with the respondent only with the complainant's consent or where required for fair investigation. Where the complainant requests anonymity, Reservo investigates as far as possible without disclosure but explains that anonymity may limit available remedies.

14. REVIEW

14.1 This policy is reviewed at least annually and after any material complaint-handling failure.

---
VERSION HISTORY
v1.0 — 17/05/2026 — Initial draft for solicitor review.

---
THIS DOCUMENT IS A DRAFT FOR PRE-LAUNCH REVIEW.`,
};